import { useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Activity, AlertTriangle, Download, RefreshCw, Users } from "lucide-react";

import { MOCK_AUDIT_EVENTS, MOCK_AUDIT_STATS } from "@/components/admin/audit/mocks";
import type {
  AuditEvent,
  AuditEventsPage,
  AuditFiltersState,
  AuditStats,
} from "@/components/admin/audit/types";
import { EMPTY_FILTERS } from "@/components/admin/audit/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ExpandableRow } from "@/components/ui/responsive-table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ACTION_COLORS: Record<string, string> = {
  "user.login": "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  "user.logout": "bg-zinc-100 text-zinc-600 dark:bg-zinc-900 dark:text-zinc-400",
  "country.update": "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  "scraping.completed":
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  "seed.completed": "bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300",
  "search.indexes_configured": "bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300",
  "user.delete_account": "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  "user.export_data": "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
  "provider.update": "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300",
};

const KNOWN_ACTIONS = [
  "user.login",
  "user.logout",
  "user.delete_account",
  "user.export_data",
  "country.update",
  "provider.update",
  "scraping.completed",
  "seed.completed",
  "search.indexes_configured",
];

const KNOWN_ENTITY_TYPES = [
  "session",
  "user",
  "country",
  "provider",
  "facility",
  "specialty",
  "scraping_job",
  "search_index",
];

export const Route = createFileRoute("/_authenticated/audit")({
  component: AuditPage,
});

function AuditPage() {
  const { t } = useLingui();
  const [filters, setFilters] = useState<AuditFiltersState>(EMPTY_FILTERS);
  const [searchQuery, setSearchQuery] = useState("");
  const [liveTail, setLiveTail] = useState(false);

  const eventsQuery = useQuery({
    queryKey: ["admin", "audit", "events", filters],
    queryFn: async (): Promise<{ page: AuditEventsPage; mock: boolean }> => {
      try {
        const params = new URLSearchParams({ limit: "100" });
        if (filters.actor) params.set("actor", filters.actor);
        if (filters.actions.length) params.set("action", filters.actions.join(","));
        if (filters.entityTypes.length) params.set("entity", filters.entityTypes.join(","));
        if (filters.since) params.set("since", filters.since);
        if (filters.until) params.set("until", filters.until);
        const data = await apiFetch.get<AuditEventsPage>(
          `/api/admin/audit/events?${params.toString()}`,
        );
        return { page: data, mock: false };
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 501)) {
          return {
            page: {
              events: MOCK_AUDIT_EVENTS,
              total: MOCK_AUDIT_EVENTS.length,
              nextCursor: null,
            },
            mock: true,
          };
        }
        throw error;
      }
    },
    refetchInterval: liveTail ? 5000 : false,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });

  const statsQuery = useQuery({
    queryKey: ["admin", "audit", "stats"],
    queryFn: async (): Promise<{ stats: AuditStats; mock: boolean }> => {
      try {
        const stats = await apiFetch.get<AuditStats>("/api/admin/audit/stats");
        return { stats, mock: false };
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 501)) {
          return { stats: MOCK_AUDIT_STATS, mock: true };
        }
        throw error;
      }
    },
    staleTime: 60_000,
  });

  const events = eventsQuery.data?.page.events ?? [];
  const isMockData = eventsQuery.data?.mock ?? false;
  const stats = statsQuery.data?.stats;

  const filteredEvents = useMemo(() => {
    if (!searchQuery) return events;
    const q = searchQuery.toLowerCase();
    return events.filter(
      (e) =>
        e.actor.toLowerCase().includes(q) ||
        e.action.toLowerCase().includes(q) ||
        (e.entityRef ?? "").toLowerCase().includes(q),
    );
  }, [events, searchQuery]);

  const exportMutation = useExportCsv(filters);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-foreground text-2xl font-bold tracking-tight">
            <Trans>Audit log</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>
              Security events, user actions, GDPR-relevant traces. Filter by actor, action, or date.
            </Trans>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLiveTail((v) => !v)}
            className="gap-1.5"
          >
            <Activity className={`size-3.5 ${liveTail ? "animate-pulse text-emerald-500" : ""}`} />
            {liveTail ? <Trans>Live tail on</Trans> : <Trans>Live tail off</Trans>}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            className="gap-1.5"
          >
            <Download className="size-3.5" />
            {exportMutation.isPending ? <Trans>Exporting…</Trans> : <Trans>Export CSV</Trans>}
          </Button>
        </div>
      </header>

      {isMockData && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          <Trans>
            Audit infrastructure pending (Track 4 W16) — UI is wired and graceful. Live data flips
            on automatically when /api/admin/audit/events lands.
          </Trans>
        </div>
      )}

      {exportMutation.isError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          <Trans>CSV export endpoint pending — V1.1 backend wire required.</Trans>
        </div>
      )}

      <StatsRow stats={stats} loading={statsQuery.isLoading} />

      <FiltersBar
        filters={filters}
        onChange={setFilters}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        loading={eventsQuery.isFetching}
      />

      <Card>
        <CardContent className="p-0">
          {eventsQuery.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-muted-foreground p-8 text-center text-sm">
              <Trans>No audit entries match the current filters.</Trans>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Trans>Timestamp</Trans>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    <Trans>Actor</Trans>
                  </TableHead>
                  <TableHead>
                    <Trans>Action</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>Entity</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>Outcome</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>IP</Trans>
                  </TableHead>
                  <TableHead className="w-8 lg:hidden" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((event) => (
                  <AuditEventRow key={event.id} event={event} placeholder={t`No payload`} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatsRow({ stats, loading }: { stats: AuditStats | undefined; loading: boolean }) {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard
        icon={<Activity className="size-4" />}
        label={<Trans>Events today</Trans>}
        value={stats.totalToday}
      />
      <StatCard
        icon={<Users className="size-4" />}
        label={<Trans>Unique actors 24h</Trans>}
        value={stats.uniqueActors24h}
      />
      <StatCard
        icon={<AlertTriangle className="size-4 text-amber-500" />}
        label={<Trans>Failed 24h</Trans>}
        value={stats.failed24h}
      />
      <Card>
        <CardContent className="space-y-1 p-3">
          <div className="text-muted-foreground inline-flex items-center gap-1.5 text-[0.625rem] tracking-wide uppercase">
            <RefreshCw className="size-3" />
            <Trans>Top action 24h</Trans>
          </div>
          {stats.topActions[0] ? (
            <div className="space-y-0.5">
              <div className="text-foreground truncate font-mono text-xs">
                {stats.topActions[0].action}
              </div>
              <div className="text-muted-foreground text-xs">
                <Trans>{stats.topActions[0].count} events</Trans>
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground text-xs">—</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  value: number;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 p-3">
        <div className="text-muted-foreground inline-flex items-center gap-1.5 text-[0.625rem] tracking-wide uppercase">
          {icon}
          {label}
        </div>
        <div className="text-foreground text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function FiltersBar({
  filters,
  onChange,
  searchQuery,
  onSearchChange,
  loading,
}: {
  filters: AuditFiltersState;
  onChange: (f: AuditFiltersState) => void;
  searchQuery: string;
  onSearchChange: (v: string) => void;
  loading: boolean;
}) {
  const { t } = useLingui();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Input
        placeholder={t`Search actor, action, entity…`}
        className="max-w-xs"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      <Input
        placeholder={t`Actor (email/phone/system)`}
        className="max-w-48"
        value={filters.actor}
        onChange={(e) => onChange({ ...filters, actor: e.target.value })}
      />
      <select
        className="border-border bg-card focus:ring-primary/30 focus:border-primary rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset"
        value={filters.actions[0] ?? ""}
        onChange={(e) => onChange({ ...filters, actions: e.target.value ? [e.target.value] : [] })}
        aria-label={t`Action filter`}
      >
        <option value="">{t`All actions`}</option>
        {KNOWN_ACTIONS.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
      <select
        className="border-border bg-card focus:ring-primary/30 focus:border-primary rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset"
        value={filters.entityTypes[0] ?? ""}
        onChange={(e) =>
          onChange({ ...filters, entityTypes: e.target.value ? [e.target.value] : [] })
        }
        aria-label={t`Entity filter`}
      >
        <option value="">{t`All entities`}</option>
        {KNOWN_ENTITY_TYPES.map((e) => (
          <option key={e} value={e}>
            {e}
          </option>
        ))}
      </select>
      <Input
        type="date"
        className="max-w-40"
        value={filters.since}
        onChange={(e) => onChange({ ...filters, since: e.target.value })}
        aria-label={t`Since`}
      />
      <Input
        type="date"
        className="max-w-40"
        value={filters.until}
        onChange={(e) => onChange({ ...filters, until: e.target.value })}
        aria-label={t`Until`}
      />
      {(filters.actor ||
        filters.actions.length > 0 ||
        filters.entityTypes.length > 0 ||
        filters.since ||
        filters.until) && (
        <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_FILTERS)}>
          <Trans>Reset</Trans>
        </Button>
      )}
      {loading && (
        <span className="text-muted-foreground self-center text-xs">
          <Trans>Loading…</Trans>
        </span>
      )}
    </div>
  );
}

function AuditEventRow({ event, placeholder }: { event: AuditEvent; placeholder: string }) {
  const [expanded, setExpanded] = useState(false);
  const colorClass =
    ACTION_COLORS[event.action] ?? "bg-zinc-100 text-zinc-600 dark:bg-zinc-900 dark:text-zinc-400";
  const outcomeColor =
    event.outcome === "success"
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
      : event.outcome === "failure"
        ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
        : "bg-zinc-100 text-zinc-600 dark:bg-zinc-900 dark:text-zinc-400";
  return (
    <>
      <ExpandableRow
        mobileDetails={[
          { label: <Trans>Actor</Trans>, value: event.actor },
          {
            label: <Trans>Type</Trans>,
            value: (
              <Badge variant="secondary" className="text-[0.625rem]">
                {event.actorType}
              </Badge>
            ),
          },
          { label: <Trans>Entity</Trans>, value: event.entityRef ?? "—" },
          {
            label: <Trans>Outcome</Trans>,
            value: (
              <Badge variant="secondary" className={outcomeColor}>
                {event.outcome}
              </Badge>
            ),
          },
          { label: <Trans>IP</Trans>, value: event.ip },
        ]}
      >
        <TableCell className="text-muted-foreground font-mono text-xs">
          {formatTimestamp(event.createdAt)}
        </TableCell>
        <TableCell className="hidden text-sm sm:table-cell">
          <div>{event.actor}</div>
          <div className="text-muted-foreground text-[10px]">{event.actorType}</div>
        </TableCell>
        <TableCell>
          <Badge variant="secondary" className={colorClass}>
            {event.action}
          </Badge>
        </TableCell>
        <TableCell className="text-muted-foreground hidden font-mono text-xs md:table-cell">
          {event.entityRef ?? "—"}
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <Badge variant="secondary" className={outcomeColor}>
            {event.outcome}
          </Badge>
        </TableCell>
        <TableCell className="text-muted-foreground hidden font-mono text-xs lg:table-cell">
          {event.ip}
        </TableCell>
      </ExpandableRow>
      <tr className="hidden md:table-row">
        <td colSpan={6} className="px-3 pb-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-primary text-xs hover:underline"
          >
            {expanded ? <Trans>Hide payload</Trans> : <Trans>Show payload</Trans>}
          </button>
          {expanded && (
            <div className="bg-muted/30 mt-2 space-y-2 rounded-md p-3">
              <div className="text-muted-foreground text-[0.625rem] tracking-wide uppercase">
                <Trans>Payload</Trans>
              </div>
              {event.payload ? (
                <pre className="bg-muted/40 max-h-72 overflow-auto rounded-md p-2 font-mono text-[11px] leading-relaxed">
                  {JSON.stringify(event.payload, null, 2)}
                </pre>
              ) : (
                <div className="text-muted-foreground text-xs">{placeholder}</div>
              )}
              <div className="text-muted-foreground space-y-0.5 pt-1 text-[11px]">
                <div>
                  <Trans>User-Agent</Trans>:{" "}
                  <span className="font-mono">{event.userAgent ?? "—"}</span>
                </div>
                <div>
                  <Trans>Event id</Trans>: <span className="font-mono">{event.id}</span>
                </div>
              </div>
            </div>
          )}
        </td>
      </tr>
    </>
  );
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return iso;
  }
}

function useExportCsv(filters: AuditFiltersState) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mutate = async () => {
    setPending(true);
    setError(null);
    try {
      const blob = await apiFetch.raw("/api/admin/audit/export", {
        method: "POST",
        body: JSON.stringify(filters),
        headers: { "Content-Type": "application/json" },
      });
      if (!blob.ok) {
        if (blob.status === 404 || blob.status === 501) {
          throw new ApiError(blob.status, "export pending");
        }
        throw new ApiError(blob.status, "export failed");
      }
      const data = await blob.blob();
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e);
    } finally {
      setPending(false);
    }
  };
  return { mutate, isPending: pending, isError: error !== null };
}
